import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  Building2, Plus, Search, MoreVertical, Pencil, Trash2, 
  Mail, Phone, Globe, DollarSign, ExternalLink 
} from "lucide-react";
import { toast } from "sonner";
import { apiClient, useAuth } from "../App";

const VENDOR_TYPES = [
  { value: "Studio", label: "Studio", color: "bg-purple-100 text-purple-800" },
  { value: "Website", label: "Website", color: "bg-blue-100 text-blue-800" },
  { value: "Marketing", label: "Marketing", color: "bg-pink-100 text-pink-800" },
  { value: "Consultant", label: "Consultant", color: "bg-amber-100 text-amber-800" },
  { value: "Other", label: "Other", color: "bg-gray-100 text-gray-800" },
];

const getTypeColor = (type) => {
  const found = VENDOR_TYPES.find(t => t.value === type);
  return found ? found.color : "bg-gray-100 text-gray-800";
};

export default function VendorManagement() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  
  const [showDialog, setShowDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: "",
    type: "Studio",
    email: "",
    phone: "",
    website: "",
    notes: ""
  });

  const isAdmin = user?.role === "COO" || user?.role === "Admin";

  useEffect(() => {
    fetchVendors();
  }, [typeFilter]);

  async function fetchVendors() {
    setLoading(true);
    try {
      const params = typeFilter !== "all" ? `?type=${typeFilter}` : "";
      const response = await apiClient.get(`/vendors${params}`);
      setVendors(response.data);
    } catch (err) {
      toast.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  }

  function openAddDialog() {
    setEditingVendor(null);
    setForm({
      name: "",
      type: "Studio",
      email: "",
      phone: "",
      website: "",
      notes: ""
    });
    setShowDialog(true);
  }

  function openEditDialog(vendor) {
    setEditingVendor(vendor);
    setForm({
      name: vendor.name || "",
      type: vendor.type || "Studio",
      email: vendor.email || "",
      phone: vendor.phone || "",
      website: vendor.website || "",
      notes: vendor.notes || ""
    });
    setShowDialog(true);
  }

  async function handleSubmit() {
    if (!form.name.trim()) {
      toast.error("Vendor name is required");
      return;
    }

    setSaving(true);
    try {
      if (editingVendor) {
        await apiClient.patch(`/vendors/${editingVendor.id}`, form);
        toast.success("Vendor updated successfully");
      } else {
        await apiClient.post("/vendors", form);
        toast.success("Vendor created successfully");
      }
      setShowDialog(false);
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save vendor");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(vendorId) {
    try {
      await apiClient.delete(`/vendors/${vendorId}`);
      toast.success("Vendor deleted");
      setShowDeleteConfirm(null);
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete vendor");
    }
  }

  const filteredVendors = vendors.filter(v =>
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    (v.email && v.email.toLowerCase().includes(search.toLowerCase()))
  );

  const totalSpend = vendors.reduce((sum, v) => sum + (v.total_spend || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="vendor-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>
            Vendor Management
          </h1>
          <p className="text-muted-foreground">Manage external vendors and studios</p>
        </div>
        <Button onClick={openAddDialog} className="rounded-full" data-testid="add-vendor-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Vendor
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Vendors</p>
                <p className="text-2xl font-light">{vendors.length}</p>
              </div>
              <Building2 className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Studios</p>
                <p className="text-2xl font-light">
                  {vendors.filter(v => v.type === "Studio").length}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Spend</p>
                <p className="text-2xl font-light">${totalSpend.toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Types</p>
                <p className="text-2xl font-light">
                  {new Set(vendors.map(v => v.type)).size}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search vendors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="vendor-search-input"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="vendor-type-filter">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {VENDOR_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Vendors Table */}
      <Card>
        <CardHeader>
          <CardTitle>Vendors ({filteredVendors.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Website</TableHead>
                <TableHead className="text-right">Total Spend</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      Loading vendors...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredVendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    {search || typeFilter !== "all" ? "No vendors match your filters" : "No vendors yet. Add your first vendor to get started."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredVendors.map(vendor => (
                  <TableRow key={vendor.id} data-testid={`vendor-row-${vendor.id}`}>
                    <TableCell>
                      <div className="font-medium">{vendor.name}</div>
                      {vendor.notes && (
                        <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                          {vendor.notes}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getTypeColor(vendor.type)}>
                        {vendor.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {vendor.email && (
                          <a 
                            href={`mailto:${vendor.email}`}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                          >
                            <Mail className="w-3 h-3" />
                            {vendor.email}
                          </a>
                        )}
                        {vendor.phone && (
                          <a 
                            href={`tel:${vendor.phone}`}
                            className="flex items-center gap-1 text-sm text-muted-foreground"
                          >
                            <Phone className="w-3 h-3" />
                            {vendor.phone}
                          </a>
                        )}
                        {!vendor.email && !vendor.phone && (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {vendor.website ? (
                        <a
                          href={vendor.website.startsWith("http") ? vendor.website : `https://${vendor.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                        >
                          <Globe className="w-3 h-3" />
                          <span className="truncate max-w-[150px]">
                            {vendor.website.replace(/^https?:\/\//, "")}
                          </span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      ${(vendor.total_spend || 0).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`vendor-menu-${vendor.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(vendor)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {isAdmin && (
                            <DropdownMenuItem
                              onClick={() => setShowDeleteConfirm(vendor)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingVendor ? "Edit Vendor" : "Add New Vendor"}
            </DialogTitle>
            <DialogDescription>
              {editingVendor 
                ? "Update vendor information" 
                : "Add a new external vendor or studio to track costs"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Vendor Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Acme Studios"
                  data-testid="vendor-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select 
                  value={form.type} 
                  onValueChange={(v) => setForm({ ...form, type: v })}
                >
                  <SelectTrigger data-testid="vendor-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VENDOR_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                  data-testid="vendor-phone-input"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="contact@vendor.com"
                  data-testid="vendor-email-input"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Website</Label>
                <Input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://vendor.com"
                  data-testid="vendor-website-input"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Notes</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Additional notes about this vendor..."
                  data-testid="vendor-notes-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-vendor-btn">
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                editingVendor ? "Save Changes" : "Create Vendor"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Vendor</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{showDeleteConfirm?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDelete(showDeleteConfirm?.id)}
              data-testid="confirm-delete-vendor-btn"
            >
              Delete Vendor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
